// @i18n-ignore-file

// Normalizes a dynamic value (label / title) into a stable, selector-safe testid
// suffix: lower-cased, non-alphanumerics collapsed to single hyphens, trimmed.
// e.g. "Women's New" -> "women-s-new"
export const slugifyLocator = (value: string | number) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const TEST_LOCATORS = {
  // product form
  SLIDER_IMAGES_MAIN_IMAGE: 'slider-images-main-image',
  CART_POPUP_PRODUCT_FORM: 'cart-popup-product-form',
  CART_POPUP_BACKDROP: 'cart-popup-backdrop',
  CART_ICON_COUNT: 'cart-icon-count',
  PRODUCT_CTA: 'product-cta',
  PRODUCT_CTA_SECTION_END: 'product-cta-section-end',
  PRODUCT_BUTTON_BAG: 'product-button-bag',
  PRODUCT_FORM_SUBSCRIPTION_TERMS: 'product-form-subscription-terms',

  // product description
  PRODUCT_DETAILS_ICON_SECTION: 'product-details-icon-section',
  PRODUCT_DETAILS_ICON: 'product-details-icon',

  //bundle form
  BUNDLE_DISCOUNT: 'bundle-discount',
  BUNDLE_ADD_TO_CART: 'bundle-add-to-cart',
  BUNDLE_PRODUCT_LIST: 'bundle-product-list',
  BUNDLE_PRODUCT_SALE_PRICE: 'bundle-product-sale-price',

  BUNDLE_OPTIONAL_PRODUCTS: 'bundle-optional-products',
  BUNDLE_OPTIONAL_PRODUCTS_DETAILS: 'bundle-optional-products-details',

  //size chart
  SIZE_CHART_BUTTON: 'size-chart-button',
  SIZE_CHART: 'size-chart',

  // reviews
  FIRST_FOLD_REVIEW_COUNT: 'first-fold-review-count',
  REVIEW_SECTION_REVIEW_COUNT: 'review-section-review-count',
  REVIEW_SECTION_WRITE_REVIEW_BUTTON: 'review-section-write-review-button',
  REVIEW_STARS_WRAPPER: 'review-stars-wrapper',

  // wishlist
  WISHLIST_ICON_COUNT: 'wishlist-icon-count',
  ADD_TO_WISHLIST_BUTTON: 'add-to-wishlist-button',
  REMOVE_FROM_WISHLIST_BUTTON: 'remove-from-wishlist-button',
  DELETE_FROM_WISHLIST_BUTTON: 'delete-from-wishlist-button',

  // Wishlist product list (DS). The grid container, one locator per item, and the
  // per-item add-to-cart / shop-in-app button; plus the empty-state container and
  // its shop-now CTA. Item locators are keyed off the compound item key
  // (`${productId}_${selectedColor}`) rather than the bare product id, because the
  // same product can appear more than once across colorways — the bare id would
  // collide (mirrors the component's React key).
  WISHLIST_LIST: {
    CONTAINER: 'wishlist-product-list',
    // e.g. ITEM('1234_Sky Blue') -> 'wishlist-product-item-1234-sky-blue'
    ITEM: (itemKey: string | number) => `wishlist-product-item-${slugifyLocator(itemKey)}`,
    // e.g. ADD_TO_CART('1234_Sky Blue') -> 'wishlist-add-to-cart-button-1234-sky-blue'
    ADD_TO_CART: (itemKey: string | number) => `wishlist-add-to-cart-button-${slugifyLocator(itemKey)}`,
    EMPTY: 'wishlist-empty',
    SHOP_NOW: 'wishlist-shop-now-button',
  },

  // collection
  COLLECTION_FILTER: 'collection-filter',
  COLLECTION_PRODUCT_COUNT: 'collection-product-count',
  COLLECTION_BREADCRUMB: 'collection-breadcrumb',
  FILTER_SECTION_APPLIED_FILTERS: 'filter-section-applied-filters',
  FILTER_SECTION_CLEAR_ALL: 'filter-section-clear-all',
  COLLECTION_INFINITE_LOADER: 'collection-infinite-loader',
  COLLECTION_TITLE: 'collection-title',
  FILTER_DRAWER_PANEL: 'filter-drawer-panel',

  // Collection filter bar (DS). Single source of truth for the filter/sort locators
  // shared by the desktop (DesktopFiltersDS) and mobile (MobileFiltersDS) bars — the
  // two mount XOR by device type, so both platforms resolve to the same selector.
  // Dynamic suffixes are slugified so option values with spaces / special characters
  // (e.g. a facet option "Bath Towels & Bundles") stay selector-safe.
  COLLECTION_FILTERS: {
    SORT_TRIGGER: 'sort-trigger',
    SORT_MENU: 'sort-menu',
    // e.g. SORT_OPTION('PRICE_LOW') -> 'sort-option-price-low'
    SORT_OPTION: (value: string | number) => `sort-option-${slugifyLocator(value)}`,
    // e.g. FILTER_CHIP_TRIGGER('product_type') -> 'filter-chip-trigger-product-type'
    FILTER_CHIP_TRIGGER: (filterName: string | number) => `filter-chip-trigger-${slugifyLocator(filterName)}`,
    CLEAR_ALL_INLINE: 'clear-all-inline-button',
    // Single-select facet radio option, keyed off the option id.
    // e.g. RADIO_OPTION('Bath Towels & Bundles') -> 'filter-radio-bath-towels-bundles'
    RADIO_OPTION: (optionId: string | number) => `filter-radio-${slugifyLocator(optionId)}`,
  },

  // product card
  PRODUCT_CARD: 'product-card',
  PRODUCT_CARD_TITLE: 'product-card-title',
  PRODUCT_CARD_SELECTED_COLOR: 'product-card-selected-color',
  PRODUCT_CARD_PRICE: 'product-card-price',

  // PDP recommendation sections
  PAIR_WITH_SECTION: 'pair_with_section',
  SIMILAR_PRODUCTS_SECTION: 'similar_products_section',
  YOU_MAY_ALSO_LIKE_SECTION: 'you_may_also_like_section',
  FREQUENTLY_BOUGHT_TOGETHER: 'frequently_bought_together_section',

  // Cart
  CART_DELETE_ITEM: 'cart-delete-item',
  CART_POPUP_CHECKOUT_BUTTON: 'cart-popup-checkout-button',
  CART_POPUP_CLOSE_BUTTON: 'cart-popup-close-button',

  // Header nav icons (rendered in both the desktop and mobile header rows)
  HEADER_CART_ICON: 'header-cart-icon',
  HEADER_SEARCH_ICON: 'header-search-icon',
  HEADER_WISHLIST_ICON: 'header-wishlist-icon',
  HEADER_ACCOUNT_ICON: 'header-account-icon',

  // Search
  NO_SEARCH_RESULTS: 'no-search-results',
  SEARCHBOX: 'searchbox',
  SEARCH_BUTTON: 'search-button',
  SEARCH_RESULTS_TITLE: 'search-results-title',

  // Global
  // Rich text
  RICH_TEXT_MOBILE: 'rich-text-mobile',
  RICH_TEXT_DESKTOP: 'rich-text-desktop',

  // Brand logo
  BRAND_LOGO: 'brand-logo',

  // Locale switcher
  LOCALISATION_CTA: 'localisation-cta',
  CHANGE_COUNTRY: 'change-country',
  LOCALE_SWITCHER_FORM: 'locale-switcher-form',
  LOCALE_SWITCHER_MODAL: 'locale-switcher-modal',
  LOCALE_SWITCHER_COUNTRY_DROPDOWN: 'locale-switcher-country-dropdown',
  LOCALE_SWITCHER_LANGUAGE_DROPDOWN: 'locale-switcher-language-dropdown',

  // Mobile menu
  MOBILE_MENU_SUBMENU: 'mobile-menu-submenu',

  // App push banner
  APP_PUSH_BANNER: 'app-push-banner',

  // Navigation + Header
  NAV: {
    NAV_MAIN: 'nav-main',
    NAV_MAIN_SPACER: 'nav-main-spacer',
    NAV_MAIN_DESKTOP: 'nav-main-desktop',
    NAV_MAIN_MOBILE: 'nav-main-mobile',
    NAV_CATEGORY_ROW: 'nav-category-row',
    NAV_CATEGORY_ROW_REVEAL: 'nav-category-row-reveal',
    NAV_SEARCH: 'nav-search',
    NAV_BANNER: 'nav-banner',
    NAV_SEARCH_BACKDROP: 'nav-search-backdrop',
    NAV_MENU_DISMISS_SPACER: 'nav-menu-dismiss-spacer',
    NAV_SEARCH_OVERLAY_MOBILE: 'nav-search-overlay-mobile',
    NAV_SEARCH_CANCEL: 'nav-search-cancel',
    NAV_SEARCH_RESULT: 'nav-search-result',
    NAV_SEARCH_RESULT_GROUP: 'nav-search-result-group',
    // Dynamic locator for a top-level nav menu item, keyed off its label so a
    // specific menu entry can be targeted regardless of position (desktop + mobile).
    // e.g. NAV_MENU_ITEM('New Arrivals') -> 'nav-menu-item-new-arrivals'
    NAV_MENU_ITEM: (value: string) => `nav-menu-item-${slugifyLocator(value)}`,
  },

  // Category showcase section (DS). Desktop + mobile carousels are both mounted
  // simultaneously and toggled via CSS, so tile locators are namespaced per
  // platform to stay unique. Each locator targets a single category chip,
  // keyed off that tile's title.
  CATEGORY_SHOWCASE: {
    CAROUSEL_DESKTOP: 'category-showcase-carousel-desktop',
    CAROUSEL_MOBILE: 'category-showcase-carousel-mobile',
    DIVIDER: 'category-showcase-divider',
    // e.g. CAROUSEL_DESKTOP_TILE('Bedding') -> 'category-showcase-carousel-desktop-tile-bedding'
    CAROUSEL_DESKTOP_TILE: (value: string | number) =>
      `category-showcase-carousel-desktop-tile-${slugifyLocator(value)}`,
    CAROUSEL_MOBILE_TILE: (value: string | number) => `category-showcase-carousel-mobile-tile-${slugifyLocator(value)}`,
  },

  // Sub-collection filter carousel (DS). Only the desktop carousel is instrumented
  // today; the locators are namespaced with a `-desktop` segment so the mobile
  // carousel (mounted simultaneously and toggled via CSS) can be added later under
  // its own `-mobile` keys without colliding. Each tile locator targets a single
  // sub-collection chip, keyed off the sub-collection id (guaranteed unique within a
  // carousel; labels can repeat, which would collide).
  SUB_COLLECTION: {
    CAROUSEL_DESKTOP: 'sub-collection-carousel-desktop',
    // e.g. CAROUSEL_DESKTOP_TILE('rugs-id') -> 'sub-collection-carousel-desktop-tile-rugs-id'
    CAROUSEL_DESKTOP_TILE: (value: string | number) => `sub-collection-carousel-desktop-tile-${slugifyLocator(value)}`,
    CAROUSEL_DESKTOP_FADE_LEFT: 'sub-collection-carousel-desktop-fade-left',
    CAROUSEL_DESKTOP_FADE_RIGHT: 'sub-collection-carousel-desktop-fade-right',
  },

  // Back-to-school recommendation widget (DS). Header shop button + one locator
  // per rendered product card, numbered 1-based and zero-padded to 2 digits.
  BACK_TO_SCHOOL: {
    // Keyed off the title text. Falls back to the bare locator when the title is empty.
    // e.g. TITLE('Back to School') -> 'recommendation-title-back-to-school'
    TITLE: (value?: string) => (value ? `recommendation-title-${slugifyLocator(value)}` : 'recommendation-title'),
    HEADER_BUTTON: 'recommendation-header-button',
    // e.g. PRODUCT(1) -> 'recommendation-product-01'
    PRODUCT: (position: number) => `recommendation-product-${String(position).padStart(2, '0')}`,
  },

  // Honest reviews section (DS). Header keyed off the heading text; one locator
  // per rendered review card, indexed 0-based.
  HONEST_REVIEWS: {
    // e.g. HEADER('Honest Reviews') -> 'review-honest-reviews'
    HEADER: (value?: string) => (value ? `review-${slugifyLocator(value)}` : 'review-header'),
    // e.g. PRODUCT(0) -> 'review-product-0'
    PRODUCT: (index: number) => `review-product-${index}`,
  },

  // Homepage banner block (DS): shared by the single hero, heroBannerV2 (multi-hero grid / carousel
  // slides), and half-banner-half-product-tiles. Bare (not keyed off title copy, which changes on
  // every campaign); when more than one banner is on the page, disambiguate via DOM order, e.g.
  // `getAllByTestId('homepage-banner-title')[n]`.
  HOMEPAGE_BANNER: {
    TITLE: 'homepage-banner-title',
  },

  // Collection promo banner (DS). Only the structured genericContents path has a distinguishable
  // title (the larger center/second item — desktop title-48, mobile title-24); the plain
  // fallbackContents path is undifferentiated body copy with no heading to key off.
  COLLECTION_BANNER: {
    TITLE: 'collection-banner-title',
  },

  // Media + text mosaic widget (DS). One locator per row, keyed off the row's position (not its
  // title copy) — CMS copy changes shouldn't change the testid, and the row index is already
  // available at the render site.
  MEDIA_TEXT_MOSAIC: {
    // e.g. TITLE(0) -> 'media-text-mosaic-title-0'
    TITLE: (index: number) => `media-text-mosaic-title-${index}`,
  },

  // Feature cards widget (DS). Band-level heading + one locator per card, keyed off the card's
  // position (not its title copy) for the same reason as MEDIA_TEXT_MOSAIC.TITLE.
  FEATURE_CARDS: {
    HEADING: 'feature-cards-heading',
    // e.g. CARD_TITLE(0) -> 'feature-cards-card-title-0'
    CARD_TITLE: (index: number) => `feature-cards-card-title-${index}`,
  },

  // Trust badges widget (DS). Band-level heading only — badges are icon + caption, not titled cards.
  TRUST_BADGES: {
    HEADING: 'trust-badges-heading',
  },

  // Half-banner-half-product-tiles widget (DS). ROOT / PRODUCT_TILE_GRID / PRODUCT_TILE_CAROUSEL
  // preserve the pre-existing hardcoded strings verbatim (now sourced from one place); PRODUCT is new
  // — one locator per rendered tile, 0-based, shared by the desktop grid and mobile carousel (same
  // tile order). The banner half's title is covered by HOMEPAGE_BANNER.TITLE (shared HeroBannerDS block).
  HALF_BANNER_HALF_PRODUCT_TILES: {
    ROOT: 'half-banner-half-product-tiles-ds',
    PRODUCT_TILE_GRID: 'product-tile-grid',
    PRODUCT_TILE_CAROUSEL: 'product-tile-carousel',
    // e.g. PRODUCT(0) -> 'half-banner-half-product-tiles-product-0'
    PRODUCT: (index: number) => `half-banner-half-product-tiles-product-${index}`,
  },

  // Personalised products recommendation widget (DS). Same grid shape as BACK_TO_SCHOOL, but the
  // header is a bare locator (not keyed off title copy, which is CMS-editable) since there's only
  // one header per widget instance — same reasoning as TRUST_BADGES.HEADING / FEATURE_CARDS.HEADING.
  // One locator per rendered product card, 1-based zero-padded.
  PERSONALISED_PRODUCTS: {
    TITLE: 'personalised-products-title',
    // e.g. PRODUCT(1) -> 'personalised-products-product-01'
    PRODUCT: (position: number) => `personalised-products-product-${String(position).padStart(2, '0')}`,
  },

  // Influencer section widgets (DS) — shared by the navy-band and stone "Quince IRL" carousels.
  // Cards are influencer/UGC tiles, not the shared ProductCard, so only the heading gets a locator.
  INFLUENCER_SECTION: {
    HEADING: 'influencer-section-heading',
  },

  // Numbered product tile inside a section lane (PDP recommendation lanes, mini-PDP collection
  // grid). The prefix is a section slug chosen so the result reads `<section>-products-NN`
  // (e.g. 'shop-the-look' -> 'shop-the-look-products-01'); it is slugified defensively but callers
  // pass kebab-case constants, never display copy. 1-based, zero-padded to 2 digits — same shape
  // as BACK_TO_SCHOOL.PRODUCT / PERSONALISED_PRODUCTS.PRODUCT.
  SECTION_PRODUCT: (sectionPrefix: string, position: number) =>
    `${slugifyLocator(sectionPrefix)}-products-${String(position).padStart(2, '0')}`,

  // PDP DS section roots (STRFRNT-16376). One locator per section root element; sections that
  // already shipped a hardcoded id keep that exact string (the value is the contract — renaming
  // would break selectors already in use). Products inside the recommendation lanes are numbered
  // via SECTION_PRODUCT with the per-section prefixes noted below.
  PDP_SECTIONS: {
    // Preserved verbatim (pre-existing hardcoded ids, now sourced from one place)
    GALLERY: 'pdp-gallery-root',
    REVIEWS: 'all-reviews-ds',
    REVIEW_HIGHLIGHTS: 'review-highlights-ds',
    // e.g. REVIEW_HIGHLIGHT_ITEM(0) -> 'review-highlight-item-0' (0-based, pre-existing shape)
    REVIEW_HIGHLIGHT_ITEM: (index: number) => `review-highlight-item-${index}`,
    PRODUCT_INFO: 'product-info',
    AI_REVIEW_SUMMARY: 'ai-review-summary-ds',
    REVIEW_SUMMARY: 'product-review-summary-ds',
    STYLITICS_WIDGET_TARGET: 'pair_with_section_left',
    NO_REVIEWS: 'no-reviews-ds',
    // Section-level wrapper present in BOTH review states (with reviews / no reviews yet), so
    // automation has one stable hook regardless of review count; REVIEWS / NO_REVIEWS remain the
    // state-specific inner roots.
    REVIEWS_SECTION: 'reviews-section',
    // New section roots
    PRODUCT_FORM: 'product-form', // the buy-box column (options + CTA) inside PRODUCT_PURCHASE
    DETAILS_AND_CARE: 'product-details-and-care',
    // Per-accordion-tab locator, keyed off the stable tab id (e.g. 'productDetails'), not the
    // translated title. e.g. DETAILS_AND_CARE_TAB('productDetails') -> 'product-details-and-care-tab-productdetails'
    DETAILS_AND_CARE_TAB: (tabId: string) => `product-details-and-care-tab-${slugifyLocator(tabId)}`,
    SHOP_THE_LOOK: 'shop-the-look', // products: SECTION_PRODUCT('shop-the-look', n)
    // Desktop Shop-the-Look modal only: custom-swiper mounts EVERY outfit slide at once (its
    // virtualOptions prop is inert), so each slide's product feed is namespaced per outfit to keep
    // ids unique — e.g. SECTION_PRODUCT(SHOP_THE_LOOK_OUTFIT(1), 2) -> 'shop-the-look-outfit-01-products-02'.
    // The mobile drawer mounts a single outfit and keeps the bare 'shop-the-look-products-NN'.
    SHOP_THE_LOOK_OUTFIT: (position: number) => `shop-the-look-outfit-${String(position).padStart(2, '0')}`,
    TRANSPARENT_PRICING: 'transparent-pricing',
    DISCLAIMER: 'pdp-disclaimer',
    // PL PDP widget roots. ACCORDION / REVIEWS_FULL / REVIEWS_MINI reuse DETAILS_AND_CARE /
    // REVIEWS_SECTION / REVIEW_HIGHLIGHTS above — the PL and non-PL pages never co-mount, so
    // sharing one id gives automation a single selector across both modes.
    BRAND_COMPARISON: 'brand-comparison',
    FEATURE_SHOWCASE: 'feature-showcase', // FeatureGridDS — also the classic FEATURE_SECTION_2 long-form DS
    FAQ: 'faq-section',
    // PL recommendation bands (HORIZONTAL_PRODUCT_LIST / PRODUCT_GRID_CAROUSEL), keyed off the
    // Statsig band id — SHOP_THE_LOOK above is reused for the shop-the-look band. Bands without a
    // mapped id share the generic RECOMMENDATIONS root; when several such bands are on one page,
    // disambiguate via DOM order (same guidance as HOMEPAGE_BANNER.TITLE). Each root doubles as the
    // band's SECTION_PRODUCT prefix, e.g. 'more-to-explore-products-01'.
    MORE_TO_EXPLORE: 'more-to-explore',
    FREQUENTLY_PURCHASED_TOGETHER: 'frequently-purchased-together',
    RECOMMENDATIONS: 'pdp-recommendations',
    // SECTION_PRODUCT prefixes for the non-PL recommendation lanes (results read `<x>-products-NN`):
    // pair-with -> 'pair-with', similar -> 'similar', YMAL -> 'you-may-also-like',
    // FBT -> 'frequently-bought-together'.
    PAIR_WITH_PRODUCT_PREFIX: 'pair-with',
    SIMILAR_PRODUCT_PREFIX: 'similar',
    YOU_MAY_ALSO_LIKE_PRODUCT_PREFIX: 'you-may-also-like',
    FREQUENTLY_BOUGHT_TOGETHER_PRODUCT_PREFIX: 'frequently-bought-together',
  },

  // Mini-PDP DS sections (STRFRNT-16384). One locator per section component root. The template's
  // collection grid reuses COLLECTION_* locators and the shared product-card ids; its numbered
  // tiles use SECTION_PRODUCT with COLLECTION_PRODUCT_PREFIX ('mini-pdp-collection-products-NN').
  // Option groups are keyed off the option name (stable API value, not display copy).
  MINI_PDP: {
    PRODUCT_INFO: 'mini-pdp-product-info',
    PRODUCT_IMAGES: 'mini-pdp-product-images',
    // e.g. IMAGE(1) -> 'mini-pdp-image-01'
    IMAGE: (position: number) => `mini-pdp-image-${String(position).padStart(2, '0')}`,
    PRODUCT_OPTIONS: 'mini-pdp-product-options',
    // e.g. OPTION_GROUP('Color') -> 'mini-pdp-option-group-color'
    OPTION_GROUP: (name: string) => `mini-pdp-option-group-${slugifyLocator(name)}`,
    PRODUCT_PURCHASE: 'mini-pdp-product-purchase',
    PRODUCT_CTA: 'mini-pdp-product-cta',
    COLLECTION_SECTION: 'mini-pdp-collection',
    COLLECTION_PRODUCT_PREFIX: 'mini-pdp-collection',
  },
}
